import {
  Bell,
  BookOpen,
  ClipboardCheck,
  CreditCard,
  Heart,
  Home,
  LifeBuoy,
  MessageCircle,
  Package,
  PackageCheck,
  Settings,
  ShoppingCart,
  Truck,
  UserCog,
  RotateCcw,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import { OrderStatusTracker } from '../components/OrderStatusTracker';
import CustomerOverview from '../components/CustomerOverview';
import TransferProcessBoard from '../components/TransferProcessBoard';
import { staticLanguageOptions } from '../../../shared/i18n';
import { useShop } from '../../../shared/store';
import { orderService } from '../../../shared/services/orderService';
import {
  isClosedOrderTrackingStatus,
  isDeliveredOrderStatus,
} from '../../../shared/utils/orderTracking';
import CustomerRefundPage from './CustomerRefundPage';
import BuyerNotificationsPanel from './BuyerNotificationsPanel';
import BuyerCreateContentPanel from '../components/BuyerCreateContentPanel';
import type { Product } from '../../../shared/types';
import { buyerNavItemsConfig, buyerSections, type BuyerSection } from '../buyerDashboardConfig';
import {
  deleteUploadedStudioLibraryItem,
  readUploadedStudioLibraryItems,
  UPLOADED_STUDIO_LIBRARY_EVENT,
  type UploadedStudioLibraryItem,
} from '../utils/uploadedStudioLibrary';

const sectionMetaKeys: Record<Exclude<BuyerSection, 'create-content'>, { titleKey: string; descriptionKey: string }> = {
  home: { titleKey: 'dashboardBuyer.sections.home.title', descriptionKey: 'dashboardBuyer.sections.home.description' },
  library: { titleKey: 'dashboardBuyer.sections.library.title', descriptionKey: 'dashboardBuyer.sections.library.description' },
  wishlist: { titleKey: 'dashboardBuyer.sections.wishlist.title', descriptionKey: 'dashboardBuyer.sections.wishlist.description' },
  cart: { titleKey: 'dashboardBuyer.sections.cart.title', descriptionKey: 'dashboardBuyer.sections.cart.description' },
  tracking: { titleKey: 'dashboardBuyer.sections.tracking.title', descriptionKey: 'dashboardBuyer.sections.tracking.description' },
  transfers: { titleKey: 'dashboardBuyer.sections.transactions.title', descriptionKey: 'dashboardBuyer.sections.transactions.description' },
  refunds: { titleKey: 'dashboardBuyer.sections.refunds.title', descriptionKey: 'dashboardBuyer.sections.refunds.description' },
  notifications: {
    titleKey: 'dashboardBuyer.sections.notifications.title',
    descriptionKey: 'dashboardBuyer.sections.notifications.description',
  },
  settings: { titleKey: 'dashboardBuyer.sections.settings.title', descriptionKey: 'dashboardBuyer.sections.settings.description' },
};

interface WishlistEntry {
  productId: string;
  quantity: number;
  addedAt?: string;
}

const WISHLIST_STORAGE_KEYS = ['haapa.wishlist', 'haapa.savedItems', 'wishlist'] as const;
const PRIMARY_WISHLIST_STORAGE_KEY = WISHLIST_STORAGE_KEYS[0];

const normalizeWishlistEntry = (entry: unknown): WishlistEntry | null => {
  if (!entry) {
    return null;
  }

  if (typeof entry === 'string') {
    const productId = entry.trim();
    return productId ? { productId, quantity: 1 } : null;
  }

  if (typeof entry !== 'object') {
    return null;
  }

  const record = entry as {
    productId?: unknown;
    id?: unknown;
    product?: { id?: unknown };
    quantity?: unknown;
    addedAt?: unknown;
  };
  const rawProductId =
    (typeof record.productId === 'string' && record.productId) ||
    (typeof record.id === 'string' && record.id) ||
    (typeof record.product?.id === 'string' && record.product.id) ||
    '';
  const productId = rawProductId.trim();
  if (!productId) {
    return null;
  }

  const quantityValue = Number(record.quantity);
  const normalizedQuantity = Number.isFinite(quantityValue) && quantityValue > 0 ? Math.floor(quantityValue) : 1;
  const addedAt = typeof record.addedAt === 'string' ? record.addedAt : undefined;

  return {
    productId,
    quantity: normalizedQuantity,
    ...(addedAt ? { addedAt } : {}),
  };
};

const dedupeWishlistEntries = (entries: WishlistEntry[]): WishlistEntry[] => {
  const byProductId = new Map<string, WishlistEntry>();
  entries.forEach((entry) => {
    if (!entry.productId.trim()) {
      return;
    }
    byProductId.set(entry.productId, {
      productId: entry.productId,
      quantity: Math.max(1, Math.floor(entry.quantity) || 1),
      addedAt: entry.addedAt,
    });
  });
  return [...byProductId.values()];
};

const readWishlistEntries = (): WishlistEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  for (const storageKey of WISHLIST_STORAGE_KEYS) {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      continue;
    }

    try {
      const parsedValue: unknown = JSON.parse(rawValue);
      const parsedArray =
        Array.isArray(parsedValue)
          ? parsedValue
          : parsedValue && typeof parsedValue === 'object' && Array.isArray((parsedValue as { items?: unknown }).items)
            ? ((parsedValue as { items?: unknown[] }).items ?? [])
            : [];
      const normalized = dedupeWishlistEntries(
        parsedArray
          .map((entry) => normalizeWishlistEntry(entry))
          .filter((entry): entry is WishlistEntry => Boolean(entry)),
      );
      if (normalized.length > 0 || storageKey === PRIMARY_WISHLIST_STORAGE_KEY) {
        return normalized;
      }
    } catch {
      continue;
    }
  }

  return [];
};

const writeWishlistEntries = (entries: WishlistEntry[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(PRIMARY_WISHLIST_STORAGE_KEY, JSON.stringify(dedupeWishlistEntries(entries)));
};

import ErrorBoundary from '../../../shared/components/ErrorBoundary';

const BuyerDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ section?: string }>();
  const activeSection = buyerSections.includes(params.section as BuyerSection)
    ? (params.section as BuyerSection)
    : 'home';
  const isCreateContentModalOpen = activeSection === 'create-content';
  const {
    currentUser,
    orders,
    cart,
    cartSubtotal,
    products,
    categories,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    refreshOrders,
  } = useShop();
  const [isCancellingOrderId, setIsCancellingOrderId] = useState<string | null>(null);
  const [cancelFeedbackByOrderId, setCancelFeedbackByOrderId] = useState<Record<string, string>>({});
  const [wishlistEntries, setWishlistEntries] = useState<WishlistEntry[]>(() => readWishlistEntries());
  const [uploadedStudioItems, setUploadedStudioItems] = useState<UploadedStudioLibraryItem[]>([]);
  const [expandedCartByProductId, setExpandedCartByProductId] = useState<Record<string, boolean>>({});
  const [expandedWishlistByProductId, setExpandedWishlistByProductId] = useState<Record<string, boolean>>({});
  const navItems = useMemo<DashboardNavItem[]>(
    () =>
      buyerNavItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? (item.labelKey ? t(item.labelKey) : item.id),
      })),
    [t],
  );
  const sectionMeta = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(sectionMetaKeys).map(([sectionKey, meta]) => [
          sectionKey,
          {
            title: t(meta.titleKey),
            description: t(meta.descriptionKey),
          },
        ]),
      ) as Record<Exclude<BuyerSection, 'create-content'>, { title: string; description: string }>,
    [t],
  );
  const activeSectionMeta =
    activeSection === 'create-content'
      ? {
          title: 'Create Content',
          description: 'Prepare a creator-ready draft from your buyer dashboard.',
        }
      : activeSection === 'transfers'
        ? {
            title: 'My Transfers',
            description: 'Track your 70% creator earnings and each transfer from your published content.',
          }
      : sectionMeta[activeSection];
  const persistWishlistEntries = useCallback((entries: WishlistEntry[]) => {
    const normalizedEntries = dedupeWishlistEntries(entries);
    writeWishlistEntries(normalizedEntries);
    setWishlistEntries(normalizedEntries);
  }, []);

  const resolveOrderTitle = useCallback(
    (order: { orderName?: string; items?: Array<{ product?: { nameKey?: string } }> }) => {
      const explicitName = typeof order.orderName === 'string' ? order.orderName.trim() : '';
      if (explicitName) {
        return explicitName;
      }

      const itemNames = (order.items ?? [])
        .map((item) => (typeof item.product?.nameKey === 'string' ? item.product.nameKey.trim() : ''))
        .filter(Boolean)
        .map((nameKey) => t(nameKey));

      if (itemNames.length === 0) {
        return 'Order';
      }

      return itemNames.join(', ');
    },
    [t],
  );

  useEffect(() => {
    setWishlistEntries(readWishlistEntries());
  }, [currentUser?.id]);

  useEffect(() => {
    setUploadedStudioItems(readUploadedStudioLibraryItems(currentUser?.id));
  }, [currentUser?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event: StorageEvent) => {
      const shouldSync =
        !event.key || (WISHLIST_STORAGE_KEYS as readonly string[]).includes(event.key);
      if (shouldSync) {
        setWishlistEntries(readWishlistEntries());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncUploadedStudioItems = () => {
      setUploadedStudioItems(readUploadedStudioLibraryItems(currentUser?.id));
    };

    syncUploadedStudioItems();
    window.addEventListener('storage', syncUploadedStudioItems);
    window.addEventListener(UPLOADED_STUDIO_LIBRARY_EVENT, syncUploadedStudioItems as EventListener);
    return () => {
      window.removeEventListener('storage', syncUploadedStudioItems);
      window.removeEventListener(UPLOADED_STUDIO_LIBRARY_EVENT, syncUploadedStudioItems as EventListener);
    };
  }, [currentUser?.id]);

  const buyerOrders = useMemo(() => {
    if (!currentUser) {
      return [];
    }

    // Backend `/orders` is already scoped to the authenticated buyer,
    // so avoid over-filtering (which can hide valid orders when the API shape differs).
    return orders;
  }, [currentUser, orders]);

  const paidBuyerOrders = useMemo(
    () =>
      buyerOrders.filter((order) => {
        const normalizedPaymentStatus = (order.paymentStatus ?? '').trim().toLowerCase();
        const isPaymentConfirmed =
          normalizedPaymentStatus === 'paid' ||
          normalizedPaymentStatus === 'successful' ||
          normalizedPaymentStatus === 'succeeded' ||
          normalizedPaymentStatus === 'success' ||
          normalizedPaymentStatus === 'completed';
        const isDeliveredCod = order.paymentMethod === 'cod' && isDeliveredOrderStatus(order.status);
        const isFreeOrder = order.total <= 0;
        return isPaymentConfirmed || isDeliveredCod || isFreeOrder;
      }),
    [buyerOrders],
  );
  const libraryItems = useMemo(
    () => {
      const uniqueItems = new Map<string, Product>();

      paidBuyerOrders.forEach((order) => {
        order.items.forEach((item, index) => {
          const product = item.product;
          const uniqueKey = product.id || `${product.nameKey}-${index}`;
          if (!uniqueItems.has(uniqueKey)) {
            uniqueItems.set(uniqueKey, product);
          }
        });
      });

      return [...uniqueItems.values()];
    },
    [paidBuyerOrders],
  );
  const wishlistProducts = useMemo(
    () => {
      const productById = new Map(products.map((product) => [product.id, product]));
      return wishlistEntries
        .map((entry) => ({
          entry,
          product: productById.get(entry.productId),
        }))
        .filter(
          (item): item is { entry: WishlistEntry; product: Product } => Boolean(item.product),
        );
    },
    [products, wishlistEntries],
  );
  const missingWishlistCount = Math.max(0, wishlistEntries.length - wishlistProducts.length);
  const savedWishlistCount = wishlistEntries.length;
  const wishlistSubtotal = useMemo(
    () => wishlistProducts.reduce((sum, item) => sum + item.product.price * item.entry.quantity, 0),
    [wishlistProducts],
  );
  const toggleCartDetails = useCallback((productId: string) => {
    setExpandedCartByProductId((previous) => ({
      ...previous,
      [productId]: !previous[productId],
    }));
  }, []);
  const toggleWishlistDetails = useCallback((productId: string) => {
    setExpandedWishlistByProductId((previous) => ({
      ...previous,
      [productId]: !previous[productId],
    }));
  }, []);
  const handleUpdateWishlistQuantity = useCallback(
    (productId: string, quantity: number) => {
      const normalizedQuantity = Math.max(1, Math.floor(quantity) || 1);
      persistWishlistEntries(
        wishlistEntries.map((entry) =>
          entry.productId === productId ? { ...entry, quantity: normalizedQuantity } : entry,
        ),
      );
    },
    [persistWishlistEntries, wishlistEntries],
  );
  const handleRemoveWishlistProduct = useCallback(
    (productId: string) => {
      persistWishlistEntries(
        wishlistEntries.filter((entry) => entry.productId !== productId),
      );
    },
    [persistWishlistEntries, wishlistEntries],
  );
  const handleMoveWishlistToCart = useCallback(
    (product: Product, quantity: number) => {
      addToCart(product, Math.max(1, Math.floor(quantity) || 1));
    },
    [addToCart],
  );
  const activeDeliveryOrders = useMemo(
    () =>
      buyerOrders.filter(
        (order) => order.fulfillmentType !== 'digital' && !isClosedOrderTrackingStatus(order.status),
      ).length,
    [buyerOrders],
  );
  const digitalLibraryCount = useMemo(
    () => libraryItems.filter((product) => (product.mediaType ?? 'book') === 'book').length,
    [libraryItems],
  );
  const audioLibraryCount = useMemo(
    () =>
      libraryItems.filter((product) => product.mediaType === 'audio').length +
      uploadedStudioItems.filter((item) => item.mediaType === 'audio').length,
    [libraryItems, uploadedStudioItems],
  );
  const uploadedStudioCount = uploadedStudioItems.length;
  const libraryCollectionCount = libraryItems.length + uploadedStudioItems.length;
  const latestBuyerOrders = useMemo(() => buyerOrders.slice(0, 3), [buyerOrders]);
  const preferredCurrency = useMemo(
    () => buyerOrders.find((order) => order.currency && order.currency.trim())?.currency ?? 'RWF',
    [buyerOrders],
  );
  const languageLabelByCode = useMemo(
    () =>
      Object.fromEntries(
        staticLanguageOptions.map((option) => [option.code, t(option.labelKey)]),
      ) as Record<string, string>,
    [t],
  );
  const currentLanguageLabel = languageLabelByCode[currentUser?.preferredLanguage ?? 'en'] ?? 'English';
  const buyerNotifications = useMemo(() => {
    const updates: string[] = [];
    updates.push(
      activeDeliveryOrders > 0
        ? `${activeDeliveryOrders} order${activeDeliveryOrders === 1 ? '' : 's'} currently in delivery progress.`
        : 'No active deliveries right now. New orders will appear here automatically.',
    );
    updates.push(
      libraryCollectionCount > 0
        ? `${libraryCollectionCount} library item${libraryCollectionCount === 1 ? '' : 's'} available across purchases and uploads.`
        : 'Your library is empty. Purchase a book or upload new media to see content here.',
    );
    updates.push('Store update: New books, audio titles, and promotions are now live.');
    return updates;
  }, [activeDeliveryOrders, libraryCollectionCount]);
  const trackedBuyerOrders = useMemo(() => buyerOrders, [buyerOrders]);

  const trackedOrder = useMemo(() => {
    const activeOrder = trackedBuyerOrders.find((order) => !isClosedOrderTrackingStatus(order.status));
    return activeOrder ?? trackedBuyerOrders[0] ?? null;
  }, [trackedBuyerOrders]);
  const trackedOrderMetaLabel = trackedOrder?.fulfillmentType === 'digital' ? 'Payment Status' : 'Expected Arrival';
  const trackingReference = trackedOrder?.paymentReference || trackedOrder?.paymentIntentId || trackedOrder?.id || 'N/A';
  const expectedArrivalDate = useMemo(() => {
    if (!trackedOrder) {
      return null;
    }

    const createdAt = new Date(trackedOrder.createdAt);
    if (Number.isNaN(createdAt.getTime())) {
      return null;
    }

    const eta = new Date(createdAt);
    eta.setDate(eta.getDate() + 5);
    return eta.toLocaleDateString();
  }, [trackedOrder]);

  useEffect(() => {
    if (!currentUser || activeSection !== 'tracking') {
      return undefined;
    }

    void refreshOrders();

    const syncOrders = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      void refreshOrders();
    };

    const intervalId = window.setInterval(syncOrders, 30000);
    window.addEventListener('focus', syncOrders);
    document.addEventListener('visibilitychange', syncOrders);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncOrders);
      document.removeEventListener('visibilitychange', syncOrders);
    };
    // refreshOrders comes from context and is recreated with provider renders.
    // Keep this effect scoped to the active tracking view and signed-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, currentUser?.id]);

  return (
    <ErrorBoundary>
      <DashboardFrame
      brand={t('dashboardBuyer.brand')}
      roleLabel={t('dashboardBuyer.roleLabel')}
      userName={currentUser?.name ?? t('dashboardBuyer.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle={activeSectionMeta.title}
      pageDescription={activeSectionMeta.description}
      navItems={navItems}
      activeNavId={activeSection}
      headerVariant="buyer"
      showSettings={false}
      headerAction={
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/create-content"
            className="rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
          >
            Create Content
          </Link>
          <Link
            to="/products"
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('dashboardBuyer.actions.browseBooks')}
          </Link>
        </div>
      }
      rightPanel={
        activeSection === 'home' ? undefined : (
          <div className="space-y-4">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                <li className="rounded-lg bg-orange-50 p-2">New announcement: Delivery now available in 3 extra cities.</li>
                <li className="rounded-lg bg-orange-50 p-2">You received 2 new recommendations based on your purchases.</li>
                <li className="rounded-lg bg-orange-50 p-2">Order update: one package moved to shipped status.</li>
              </ul>
            </article>
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Quick Profile</h3>
              <p className="mt-2 text-sm font-semibold text-slate-800">{currentUser?.name ?? 'Reader'}</p>
              <p className="text-xs text-slate-500">{currentUser?.email ?? 'reader@haapa.store'}</p>
              <p className="mt-3 text-xs text-slate-600">Language: English</p>
              <p className="text-xs text-slate-600">Currency: RWF</p>
            </article>
          </div>
        )
      }
    >
      {activeSection === 'home' && (
        <div className="space-y-4">
          <CustomerOverview
            myBooks={libraryItems.filter((p) => p.mediaType === 'book')}
            purchasedProducts={libraryItems}
            cartItems={cart.map((item) => item.product)}
            favoriteProducts={wishlistProducts.map((item) => item.product)}
            cartCount={cart.length}
            wishlistCount={wishlistEntries.length}
          />

          <div className="grid gap-4 xl:grid-cols-2">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900">Order Tracking</h3>
                <Link
                  to="/dashboard/tracking"
                  className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                >
                  View tracking
                </Link>
              </div>
              {latestBuyerOrders.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">No purchases yet. Once you buy, tracking and delivery progress will appear here.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {latestBuyerOrders.map((order) => (
                    <article key={order.id} className="rounded-xl bg-orange-50 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-slate-800">Order {order.id}</p>
                        <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-orange-700">{order.status}</span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{new Date(order.createdAt).toLocaleString()}</p>
                      <div className="mt-3">
                        <OrderStatusTracker status={order.status} history={order.statusHistory} />
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900">Library Access</h3>
                <Link
                  to="/dashboard/library"
                  className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                >
                  Open library
                </Link>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                {libraryCollectionCount > 0
                  ? 'Your purchased content and uploaded studio media are available in your account.'
                  : 'You have not unlocked or uploaded any digital media yet.'}
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-4">
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">All Items</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{libraryCollectionCount}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Digital Books</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{digitalLibraryCount}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Audio Titles</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{audioLibraryCount}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">My Uploads</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{uploadedStudioCount}</p>
                </div>
              </div>
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-slate-900">Cart & Wishlist Visibility</h3>
                <Link
                  to="/dashboard/cart"
                  className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                >
                  Open cart
                </Link>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Pending in Cart</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{cart.length}</p>
                  <p className="mt-1 text-xs text-slate-500">Subtotal: RWF {cartSubtotal.toFixed(2)}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Saved / Wishlist</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{savedWishlistCount}</p>
                  <p className="mt-1 text-xs text-slate-500">Saved items ready for checkout</p>
                </div>
              </div>
              <Link
                to="/books"
                className="mt-3 inline-flex rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
              >
                Continue shopping
              </Link>
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-orange-600" />
                <h3 className="text-base font-semibold text-slate-900">Notifications</h3>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                {buyerNotifications.map((notification) => (
                  <li key={notification} className="rounded-lg bg-orange-50 p-2">
                    {notification}
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4 text-orange-600" />
                <h3 className="text-base font-semibold text-slate-900">Profile & Settings</h3>
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-800">{currentUser?.name ?? 'Reader'}</p>
              <p className="text-xs text-slate-500">{currentUser?.email ?? 'reader@haapa.store'}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <p className="rounded-lg bg-orange-50 px-3 py-2 text-xs text-slate-700">Language: {currentLanguageLabel}</p>
                <p className="rounded-lg bg-orange-50 px-3 py-2 text-xs text-slate-700">Currency: {preferredCurrency}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/dashboard/settings"
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Open settings
                </Link>
                <Link
                  to="/profile"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Edit profile
                </Link>
              </div>
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <div className="flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 text-orange-600" />
                <h3 className="text-base font-semibold text-slate-900">Support Access</h3>
              </div>
              <p className="mt-3 text-sm text-slate-600">Raise payment, report, or role issues directly from your dashboard overview.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href="mailto:support@haapa.store?subject=Payment%20Issue&body=Hello%20Haapa%20Support,%0AOrder%20ID:%20"
                  className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Payment issue
                </a>
                <a
                  href="mailto:support@haapa.store?subject=Report%20Issue&body=Hello%20Haapa%20Support,%0AI%20want%20to%20report%20an%20issue:%20"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Report content
                </a>
                <a
                  href="mailto:support@haapa.store?subject=Account%20Role%20Support&body=Hello%20Haapa%20Support,%0APlease%20help%20with%20my%20account%20role:%20"
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  Roles support
                </a>
              </div>
              <p className="mt-2 text-xs text-slate-500">Or use live chat from the website footer for faster help.</p>
            </article>
          </div>
        </div>
      )}

      {activeSection === 'library' && (
        <div className="space-y-4 pb-2">
          {libraryItems.length === 0 && uploadedStudioItems.length === 0 ? (
            <article className="rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
              No paid or uploaded items in your library yet. Complete payment or upload new media to see content here.
            </article>
          ) : (
            <div className="space-y-5">
              {uploadedStudioItems.length > 0 ? (
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">Uploaded Studio Media</h3>
                      <p className="text-sm text-slate-500">Audio and video you uploaded from the creator studios.</p>
                    </div>
                    <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">{uploadedStudioItems.length} item{uploadedStudioItems.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {uploadedStudioItems.map((item) => (
                      <article key={item.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                        <img 
                          src={item.imageUrl ?? 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80'} 
                          alt={item.title} 
                          className="h-36 w-full object-cover" 
                        />
                        <div className="space-y-2 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">{item.mediaType === 'audio' ? 'Uploaded Audio' : 'Uploaded Video'}</p>
                          <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
                          <p className="text-xs text-slate-500">{new Date(item.uploadedAt).toLocaleString()}</p>
                          <div className="flex flex-wrap gap-2 pt-1">
                            <a
                              href={item.mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                            >
                              Open Media
                            </a>
                            {item.backendUrl ? (
                              <a
                                href={item.backendUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                              >
                                Open Upload
                              </a>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => deleteUploadedStudioLibraryItem(item.id)}
                              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}

              {libraryItems.length > 0 ? (
                <section className="space-y-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Purchased Library</h3>
                    <p className="text-sm text-slate-500">Books and audio you unlocked through paid orders.</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {libraryItems.map((product, index) => (
                      <article key={`${product.id}-${index}`} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                        <img 
                          src={product.imageUrl ?? 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80'} 
                          alt={t(product.nameKey)} 
                          className="h-36 w-full object-cover" 
                        />
                        <div className="space-y-2 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">{t(product.categoryKey)}</p>
                          <h3 className="text-base font-semibold text-slate-900">{t(product.nameKey)}</h3>
                          {(product.mediaType ?? 'book') === 'book' ? (
                            <Link
                              to={`/products/${product.id}/pageview`}
                              className="inline-flex rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                            >
                              Read
                            </Link>
                          ) : (
                            <Link
                              to={`/products/${product.id}`}
                              className="inline-flex rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                            >
                              Open
                            </Link>
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </div>
      )}

      {activeSection === 'wishlist' && (
        <div className="space-y-4 pb-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">My Wishlist</h3>
            {wishlistProducts.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">No products saved in wishlist yet.</p>
            ) : (
              <div className="mt-3 space-y-3">
                {wishlistProducts.map(({ entry, product }) => (
                  <article key={product.id} className="rounded-xl bg-orange-50 p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <img src={product.imageUrl} alt={t(product.nameKey)} className="h-24 w-24 rounded-lg object-cover" />
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900">{t(product.nameKey)}</h4>
                        <p className="mt-1 text-xs text-slate-600 line-clamp-2">{t(product.descriptionKey)}</p>
                        <p className="mt-1 text-sm font-semibold text-orange-700">RWF {product.price.toFixed(2)}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <label className="grid gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Qty
                            <input
                              type="number"
                              min={1}
                              value={entry.quantity}
                              onChange={(event) =>
                                handleUpdateWishlistQuantity(product.id, Math.max(1, Number(event.target.value) || 1))
                              }
                              className="w-20 rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleWishlistDetails(product.id)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                          >
                            {expandedWishlistByProductId[product.id] ? 'Hide details' : 'View more'}
                          </button>
                          <Link
                            to={`/products/${product.id}`}
                            className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                          >
                            Open product
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleMoveWishlistToCart(product, entry.quantity)}
                            className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600"
                          >
                            Add to cart
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveWishlistProduct(product.id)}
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        {expandedWishlistByProductId[product.id] && (
                          <dl className="mt-3 grid gap-2 rounded-lg border border-orange-100 bg-white p-3 text-xs text-slate-600 sm:grid-cols-2">
                            <div><dt className="font-semibold text-slate-700">Category</dt><dd>{t(product.categoryKey)}</dd></div>
                            <div><dt className="font-semibold text-slate-700">Media</dt><dd>{product.mediaType ?? 'book'}</dd></div>
                            <div><dt className="font-semibold text-slate-700">Stock</dt><dd>{product.stock}</dd></div>
                            <div><dt className="font-semibold text-slate-700">Rating</dt><dd>{product.rating.toFixed(1)} ({product.reviewCount} reviews)</dd></div>
                            <div><dt className="font-semibold text-slate-700">Product ID</dt><dd className="break-all">{product.id}</dd></div>
                            <div><dt className="font-semibold text-slate-700">Total</dt><dd>RWF {(product.price * entry.quantity).toFixed(2)}</dd></div>
                          </dl>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {missingWishlistCount > 0 && (
              <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
                {missingWishlistCount} saved wishlist item{missingWishlistCount === 1 ? '' : 's'} could not be matched with current catalog.
              </p>
            )}
          </article>
        </div>
      )}

      {activeSection === 'cart' && (
        <div className="grid gap-4 pb-2 xl:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-4">
            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-900">Cart Items</h3>
              {cart.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">Your cart is currently empty.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {cart.map((item) => (
                    <article key={item.product.id} className="rounded-xl bg-orange-50 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                        <img src={item.product.imageUrl} alt={t(item.product.nameKey)} className="h-24 w-24 rounded-lg object-cover" />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-slate-900">{t(item.product.nameKey)}</h4>
                          <p className="mt-1 text-xs text-slate-600 line-clamp-2">{t(item.product.descriptionKey)}</p>
                          <p className="mt-1 text-sm font-semibold text-orange-700">RWF {item.product.price.toFixed(2)}</p>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <label className="grid gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Qty
                              <input
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(event) =>
                                  updateCartQuantity(item.product.id, Math.max(1, Number(event.target.value) || 1))
                                }
                                className="w-20 rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => toggleCartDetails(item.product.id)}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                            >
                              {expandedCartByProductId[item.product.id] ? 'Hide details' : 'View more'}
                            </button>
                            <Link
                              to={`/products/${item.product.id}`}
                              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                            >
                              Open product
                            </Link>
                            <button
                              type="button"
                              onClick={() => removeFromCart(item.product.id)}
                              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                          {expandedCartByProductId[item.product.id] && (
                            <dl className="mt-3 grid gap-2 rounded-lg border border-orange-100 bg-white p-3 text-xs text-slate-600 sm:grid-cols-2">
                              <div><dt className="font-semibold text-slate-700">Category</dt><dd>{t(item.product.categoryKey)}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Media</dt><dd>{item.product.mediaType ?? 'book'}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Stock</dt><dd>{item.product.stock}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Rating</dt><dd>{item.product.rating.toFixed(1)} ({item.product.reviewCount} reviews)</dd></div>
                              <div><dt className="font-semibold text-slate-700">Product ID</dt><dd className="break-all">{item.product.id}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Line Total</dt><dd>RWF {(item.product.price * item.quantity).toFixed(2)}</dd></div>
                            </dl>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </article>

            <article className="rounded-2xl border border-orange-100 bg-white p-4">
              <h3 className="text-base font-semibold text-slate-900">Wishlist</h3>
              {wishlistProducts.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">No products saved in wishlist yet.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {wishlistProducts.map(({ entry, product }) => (
                    <article key={product.id} className="rounded-xl bg-orange-50 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                        <img src={product.imageUrl} alt={t(product.nameKey)} className="h-24 w-24 rounded-lg object-cover" />
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-slate-900">{t(product.nameKey)}</h4>
                          <p className="mt-1 text-xs text-slate-600 line-clamp-2">{t(product.descriptionKey)}</p>
                          <p className="mt-1 text-sm font-semibold text-orange-700">RWF {product.price.toFixed(2)}</p>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <label className="grid gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Qty
                              <input
                                type="number"
                                min={1}
                                value={entry.quantity}
                                onChange={(event) =>
                                  handleUpdateWishlistQuantity(product.id, Math.max(1, Number(event.target.value) || 1))
                                }
                                className="w-20 rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => toggleWishlistDetails(product.id)}
                              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                            >
                              {expandedWishlistByProductId[product.id] ? 'Hide details' : 'View more'}
                            </button>
                            <Link
                              to={`/products/${product.id}`}
                              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-100"
                            >
                              Open product
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleMoveWishlistToCart(product, entry.quantity)}
                              className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-orange-600"
                            >
                              Update & Add to cart
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveWishlistProduct(product.id)}
                              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                          {expandedWishlistByProductId[product.id] && (
                            <dl className="mt-3 grid gap-2 rounded-lg border border-orange-100 bg-white p-3 text-xs text-slate-600 sm:grid-cols-2">
                              <div><dt className="font-semibold text-slate-700">Category</dt><dd>{t(product.categoryKey)}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Media</dt><dd>{product.mediaType ?? 'book'}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Stock</dt><dd>{product.stock}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Rating</dt><dd>{product.rating.toFixed(1)} ({product.reviewCount} reviews)</dd></div>
                              <div><dt className="font-semibold text-slate-700">Product ID</dt><dd className="break-all">{product.id}</dd></div>
                              <div><dt className="font-semibold text-slate-700">Saved Qty Total</dt><dd>RWF {(product.price * entry.quantity).toFixed(2)}</dd></div>
                            </dl>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              {missingWishlistCount > 0 && (
                <p className="mt-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
                  {missingWishlistCount} saved wishlist item{missingWishlistCount === 1 ? '' : 's'} could not be matched with current catalog.
                </p>
              )}
            </article>
          </div>

          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Checkout Summary</h3>
            <p className="mt-3 text-sm text-slate-600">Cart subtotal: RWF {cartSubtotal.toFixed(2)}</p>
            <p className="text-sm text-slate-600">Wishlist total (saved): RWF {wishlistSubtotal.toFixed(2)}</p>
            <p className="text-sm text-slate-600">Delivery fee: RWF 1500</p>
            <p className="mt-2 text-sm font-semibold text-slate-800">Estimated total: RWF {(cartSubtotal + 1500).toFixed(2)}</p>
            <Link
              to="/checkout"
              className="mt-4 inline-flex rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white"
            >
              Proceed to Checkout
            </Link>
          </article>
        </div>
      )}

      {activeSection === 'tracking' && (
        <div className="space-y-4 pb-2">
          {trackedOrder ? (
            <>
              <article className="relative overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-white via-[#fcfcfc] to-slate-50 p-5 shadow-[0_20px_45px_-30px_rgba(249,115,22,0.55)] sm:p-6">
              <div className="pointer-events-none absolute inset-0">
                <div
                  className="absolute -left-24 -top-24 h-64 w-64 opacity-55"
                  style={{
                    backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.28) 1.4px, transparent 1.4px)',
                    backgroundSize: '13px 13px',
                    maskImage: 'radial-gradient(circle at 60% 60%, rgba(0,0,0,1), transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 60% 60%, rgba(0,0,0,1), transparent 78%)',
                    transform: 'rotate(11deg)',
                  }}
                />
                <div
                  className="absolute -bottom-28 -right-24 h-72 w-72 opacity-50"
                  style={{
                    backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.24) 1.4px, transparent 1.4px)',
                    backgroundSize: '13px 13px',
                    maskImage: 'radial-gradient(circle at 40% 40%, rgba(0,0,0,1), transparent 80%)',
                    WebkitMaskImage: 'radial-gradient(circle at 40% 40%, rgba(0,0,0,1), transparent 80%)',
                    transform: 'rotate(-13deg)',
                  }}
                />
              </div>

              <div className="relative z-10">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Order</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">
                    <span className="text-orange-700">{resolveOrderTitle(trackedOrder)}</span>
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{trackedOrderMetaLabel}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">
                    {trackedOrder.fulfillmentType === 'digital' ? trackedOrder.status : expectedArrivalDate ?? 'To be confirmed'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Ref: {trackingReference}</p>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                <OrderStatusTracker
                  status={trackedOrder.status}
                  history={trackedOrder.statusHistory}
                  variant="detailed"
                />
              </div>
              </div>
              </article>

              <div className="grid gap-3">
                {trackedBuyerOrders.map((order) => (
                  <article key={order.id} className="rounded-2xl border border-orange-100 bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">{resolveOrderTitle(order)}</h3>
                      <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">{order.status}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{new Date(order.createdAt).toLocaleString()}</p>
                    <div className="mt-4">
                      <OrderStatusTracker status={order.status} history={order.statusHistory} variant="detailed" />
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : trackedBuyerOrders.length > 0 ? (
            trackedBuyerOrders.map((order) => (
              <article key={order.id} className="rounded-2xl border border-orange-100 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">{resolveOrderTitle(order)}</h3>
                  <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-700">{order.status}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{new Date(order.createdAt).toLocaleString()}</p>
                <div className="mt-4">
                  <OrderStatusTracker status={order.status} history={order.statusHistory} variant="detailed" />
                </div>

                {cancelFeedbackByOrderId[order.id] && (
                  <p className="mt-3 text-xs font-semibold text-rose-600">{cancelFeedbackByOrderId[order.id]}</p>
                )}

                {order.status === 'Processed' && (
                  <button
                    type="button"
                    disabled={isCancellingOrderId === order.id}
                    onClick={() => {
                      setCancelFeedbackByOrderId((prev) => ({ ...prev, [order.id]: '' }));
                      setIsCancellingOrderId(order.id);
                      void (async () => {
                        try {
                          await orderService.cancelOrder(order.id);
                          await refreshOrders();
                        } catch (error: unknown) {
                          const err = error as { message?: string };
                          setCancelFeedbackByOrderId((prev) => ({
                            ...prev,
                            [order.id]: err.message || 'Unable to cancel order.',
                          }));
                        } finally {
                          setIsCancellingOrderId(null);
                        }
                      })();
                    }}
                    className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isCancellingOrderId === order.id ? 'Cancelling...' : 'Cancel Order'}
                  </button>
                )}
              </article>
            ))
          ) : (
            <article className="rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
              No orders available yet for tracking.
            </article>
          )}
        </div>
      )}

      {activeSection === 'transfers' && <TransferProcessBoard role="buyer" />}

      {activeSection === 'refunds' && <CustomerRefundPage />}

      {activeSection === 'notifications' && <BuyerNotificationsPanel />}

      {activeSection === 'settings' && (
        <div className="grid gap-4 pb-2 md:grid-cols-2">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Profile Settings</h3>
            <div className="mt-3 grid gap-3 text-sm">
              <input defaultValue={currentUser?.name ?? ''} className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <input defaultValue={currentUser?.email ?? ''} className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none" />
              <button type="button" className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white">Save Profile</button>
            </div>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-900">Preferences</h3>
            <div className="mt-3 grid gap-3 text-sm">
              <select className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none">
                <option>English</option>
                <option>French</option>
                <option>Swahili</option>
              </select>
              <select className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none">
                <option>RWF</option>
              </select>
              <button type="button" className="rounded-xl border border-orange-200 px-4 py-2 text-sm font-semibold text-orange-700">
                Update Preferences
              </button>
            </div>
          </article>
        </div>
      )}

      {isCreateContentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <article className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-orange-100 bg-white p-5 shadow-[0_35px_80px_-35px_rgba(15,23,42,0.45)] sm:p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-orange-600">Buyer Dashboard</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">Create Content</h2>
              </div>
              <button
                type="button"
                onClick={() => navigate('/dashboard/home')}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="mt-5">
              <BuyerCreateContentPanel categories={categories} />
            </div>
          </article>
        </div>
      )}
    </DashboardFrame>
    </ErrorBoundary>
  );
};

export default BuyerDashboardPage;
